/**
 * Central role predicates for organization members.
 *
 * Before this module, "does this role see every client in the org, or only their own
 * roster" was re-inlined at every call site as
 * `roleName === "Owner" || roleName === "Admin" || roleName === "Nutritionist"` — repeated
 * across ~95 files. A role that isn't in one of those hand-written lists silently falls
 * through the cracks (loses a screen it should see, or worse, doesn't get excluded from an
 * Admin-only action). Import these instead of re-inlining the check.
 *
 * `isOrganizationMember` deliberately does NOT enumerate role names (see below) — that's
 * the one predicate a brand new role passes automatically, with no file to update.
 */

const ADMIN_LEVEL_ROLES = new Set(["Owner", "Admin"]);
const ORG_WIDE_CLIENT_ROLES = new Set([
  "Owner",
  "Admin",
  "Nutritionist",
  "Team Coach",
]);
const MEMBER_MANAGEMENT_ROLES = new Set(["Owner", "Admin", "Team Coach"]);
const TASK_LIST_ALL_ATHLETES_ROLES = new Set(["Owner", "Admin", "Team Coach"]);

/**
 * Owner or Admin — full org-wide read/write plus team management, today's two most
 * privileged roles.
 * @param {string} roleName
 * @returns {boolean}
 */
export const isAdminOrOwner = (roleName) => ADMIN_LEVEL_ROLES.has(roleName);

/**
 * Every role that sees every client in the organization rather than only their own
 * assigned roster (Owner, Admin, Nutritionist, Team Coach today).
 * @param {string} roleName
 * @returns {boolean}
 */
export const canViewAllOrganizationClients = (roleName) =>
  ORG_WIDE_CLIENT_ROLES.has(roleName);

/**
 * Owner, Admin, or Team Coach — the roles allowed to load every athlete in the
 * organization for the task list (Nutritionist is deliberately excluded here, unlike
 * `canViewAllOrganizationClients` — this gates a narrower, admin-level feature, not client
 * scope in general).
 * @param {string} roleName
 * @returns {boolean}
 */
export const canLoadAllOrganizationAthletesForTasks = (roleName) =>
  TASK_LIST_ALL_ATHLETES_ROLES.has(roleName);

/**
 * True for any user holding an organization role. Deliberately `!!roleName` rather than an
 * enumerated list: `roleName` only ever holds a real, resolved role name (see
 * getRoleNameById) or a falsy value when unresolved, so this is exactly "has a role" and
 * needs no update when a role is added — unlike an enumerated list, which silently excludes
 * anything not in it (that bug already exists at Chat.js's isOrganizationMember).
 * @param {string} roleName
 * @returns {boolean}
 */
export const isOrganizationMember = (roleName) => !!roleName;

/**
 * True only for a role that has an organization but is limited to their own assigned
 * clients (Coach today) — i.e. an org member who does NOT see the whole org's client base.
 * @param {string} roleName
 * @returns {boolean}
 */
export const hasOwnAssignedClientsOnly = (roleName) =>
  isOrganizationMember(roleName) && !canViewAllOrganizationClients(roleName);

/**
 * Roles that see the Manage Coaches actions at all (invite, edit, remove, resend). Whether
 * one of them may act on a specific member is `canManageOrganizationMember`.
 * @param {string} roleName
 * @returns {boolean}
 */
export const canManageOrganizationMembers = (roleName) =>
  MEMBER_MANAGEMENT_ROLES.has(roleName);

/**
 * Whether `callerRoleName` may edit or remove a member holding `targetRoleName`. Owner
 * manages everyone but the Owner, Admin everyone but Admin/Owner, and a Team Coach the
 * same as an Admin except itself — the same split `getRolesToShow` applies to who can be
 * invited.
 * @param {string} callerRoleName
 * @param {string} targetRoleName
 * @param {boolean} [isSelf]
 * @returns {boolean}
 */
export const canManageOrganizationMember = (
  callerRoleName,
  targetRoleName,
  isSelf = false
) => {
  if (callerRoleName === "Owner") return targetRoleName !== "Owner";
  if (callerRoleName === "Admin") {
    return targetRoleName !== "Admin" && targetRoleName !== "Owner";
  }
  if (callerRoleName === "Team Coach") {
    return !isSelf && targetRoleName !== "Admin" && targetRoleName !== "Owner";
  }
  return false;
};

/**
 * Whether `callerRoleName` may remove a member — `canManageOrganizationMember` minus the
 * Team Coach, who can edit members but not remove them.
 * @param {string} callerRoleName
 * @param {string} targetRoleName
 * @param {boolean} [isSelf]
 * @returns {boolean}
 */
export const canRemoveOrganizationMember = (
  callerRoleName,
  targetRoleName,
  isSelf = false
) =>
  callerRoleName !== "Team Coach" &&
  canManageOrganizationMember(callerRoleName, targetRoleName, isSelf);
